import { Temporal } from 'temporal-polyfill';
import { useClock } from '@/contexts/ClockContext';
import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/lib/utils';
import type { DomainTemporal } from '@/models/entities/lead';

interface TemporalFormatterProps {
  readonly value:
    | DomainTemporal
    | Temporal.Instant
    | Temporal.PlainDate
    | Temporal.ZonedDateTime
    | null
    | undefined;
  readonly className?: string;
  readonly options?: Intl.DateTimeFormatOptions;
  readonly nowrap?: boolean;
}

const defaultOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

export function TemporalFormatter({
  value,
  className,
  options,
  nowrap = true,
}: Readonly<TemporalFormatterProps>) {
  const { locale } = useLocale();
  const { clock } = useClock();

  if (!value) return null;

  const resolvedOptions: Intl.DateTimeFormatOptions = {
    ...defaultOptions,
    ...options,
  };

  const finalOptions: Intl.DateTimeFormatOptions =
    value instanceof Temporal.ZonedDateTime || resolvedOptions.timeZone
      ? resolvedOptions
      : {
          ...resolvedOptions,
          timeZone: clock.getTimeZone(),
        };

  return (
    <time
      dateTime={value.toString()}
      className={cn(nowrap && 'whitespace-nowrap', className)}
    >
      {value.toLocaleString(locale, finalOptions)}
    </time>
  );
}
